// frontend/src/pages/ResetPassword.tsx
//
// Bu sayfa e-postadaki şifre sıfırlama linkine tıklandığında açılır.
// ProtectedRoute dışındadır — Supabase, PASSWORD_RECOVERY oturumunu
// otomatik açar ve bu sayfa o olayı yakalayarak yeni şifre formunu sunar.

import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { authService } from '../services/auth.service';
import { useAuthStore } from '../store/authStore';
import { Check, X, Lock, KeyRound, Eye, EyeOff } from 'lucide-react';
import '../styles/auth.css';

export function ResetPasswordPage() {
  const navigate = useNavigate();

  const [ready, setReady] = useState(false);
  const [expired, setExpired] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isMinLength = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  const isPasswordStrong = isMinLength && hasUpper && hasNumber && hasSpecial;

  useEffect(() => {
    // PASSWORD_RECOVERY olayını dinle — bu sayfa sadece bu olayla etkinleşmeli.
    // Normal kayıt doğrulama mailleri de bir session getirir, ama biz onları
    // buraya yönlendirmemeli; onların URL'inde type=recovery OLMAZ.
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session) {
        setReady(true);
        setExpired(false);
      }
    });

    // 5 saniye sonra hâlâ hazır değilse token geçersiz / yanlış akış
    const timer = setTimeout(() => {
      setReady((prev) => {
        if (!prev) setExpired(true);
        return prev;
      });
    }, 5000);

    return () => {
      listener?.subscription?.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!isPasswordStrong) {
      setError('Şifreniz güvenlik politikalarına uymuyor. Lütfen tüm kriterleri karşılayın.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Girdiğiniz şifreler eşleşmiyor.');
      return;
    }

    setIsLoading(true);
    try {
      await authService.updatePassword(password);
      setSuccess(true);
      setTimeout(async () => {
        useAuthStore.getState().setRecovery(false);
        await authService.signOut();
        navigate('/auth?mode=login', { replace: true });
      }, 3000);
    } catch (err: any) {
      setError(
        err?.message ||
          'Şifre güncellenirken bir hata oluştu. Bağlantı süresi dolmuş veya daha önce kullanılmış olabilir.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-glow-top" />
      <div className="auth-glow-bottom" />

      <div className="auth-card" style={{ maxWidth: 480 }}>
        <div className="auth-header">
          <div className="auth-logo-row">
            <svg width="44" height="44" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="auth-logo-svg">
              <defs>
                <linearGradient id="rpBlue" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#38BDF8" />
                  <stop offset="100%" stopColor="#0369A1" />
                </linearGradient>
                <linearGradient id="rpTeal" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#17C79A" />
                  <stop offset="100%" stopColor="#0B8F70" />
                </linearGradient>
              </defs>
              <path d="M 35 150 L 35 60 L 80 110 L 110 80 L 145 120 L 145 150" stroke="url(#rpBlue)" strokeWidth="18" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M 110 60 L 155 60 C 170 60 170 85 150 100 L 115 125 L 155 170" stroke="url(#rpTeal)" strokeWidth="18" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="95" cy="95" r="14" fill="#17C79A" />
            </svg>
            <div className="auth-logo-text-col">
              <span className="auth-logo-main">Minik<span className="auth-logo-highlight">Coder</span></span>
              <span className="auth-logo-sub">AKADEMİ</span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20 }}>
            <KeyRound size={22} color="#0B8F70" />
            <h2 className="auth-title" style={{ margin: 0 }}>Yeni Şifre Belirle</h2>
          </div>
          <p className="auth-subtitle">Hesabınız için güçlü ve güvenli yeni bir şifre oluşturun</p>
        </div>

        {/* Token süresi dolmuş */}
        {expired && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⏱️</div>
            <h3 style={{ color: '#EF4444', marginBottom: 8 }}>Bağlantı Süresi Dolmuş</h3>
            <p style={{ color: '#6E6549', marginBottom: 20, lineHeight: 1.6 }}>
              Bu şifre sıfırlama bağlantısı geçersiz veya süresi dolmuş. Lütfen yeni bir sıfırlama bağlantısı talep edin.
            </p>
            <button
              className="auth-submit-btn"
              onClick={() => navigate('/auth?mode=forgot-password', { replace: true })}
            >
              Yeni Bağlantı Talep Et
            </button>
          </div>
        )}

        {/* Yükleniyor */}
        {!expired && !ready && !success && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{
              width: 36, height: 36, border: '4px solid rgba(23,199,154,0.2)',
              borderTopColor: '#17C79A', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite', margin: '0 auto 16px'
            }} />
            <p style={{ color: '#6E6549' }}>Sıfırlama bağlantısı doğrulanıyor...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Başarı */}
        {success && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <h3 style={{ color: '#0B8F70', marginBottom: 8 }}>Şifreniz Başarıyla Güncellendi!</h3>
            <p style={{ color: '#6E6549', lineHeight: 1.6 }}>
              Yeni şifrenizle giriş yapabilirsiniz. Giriş sayfasına yönlendiriliyorsunuz...
            </p>
          </div>
        )}

        {/* Form */}
        {ready && !success && !expired && (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-input-group">
              <label>Yeni Şifre *</label>
              <div className="auth-input-wrapper">
                <Lock className="auth-input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="En az 8 karakter..."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="auth-input-group">
              <label>Yeni Şifre (Tekrar) *</label>
              <div className="auth-input-wrapper">
                <Lock className="auth-input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Şifreyi tekrar girin..."
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="password-policy-box">
              <div className="password-policy-title">Güvenlik Politikası Kriterleri:</div>
              <div className="password-policy-list">
                <div className={`password-policy-item ${isMinLength ? 'valid' : ''}`}>
                  {isMinLength ? <Check size={14} /> : <X size={14} />} En az 8 karakter
                </div>
                <div className={`password-policy-item ${hasUpper ? 'valid' : ''}`}>
                  {hasUpper ? <Check size={14} /> : <X size={14} />} En az 1 büyük harf (A-Z)
                </div>
                <div className={`password-policy-item ${hasNumber ? 'valid' : ''}`}>
                  {hasNumber ? <Check size={14} /> : <X size={14} />} En az 1 rakam (0-9)
                </div>
                <div className={`password-policy-item ${hasSpecial ? 'valid' : ''}`}>
                  {hasSpecial ? <Check size={14} /> : <X size={14} />} En az 1 özel karakter (!@#$%^&*)
                </div>
              </div>
            </div>

            {error && <p className="auth-error">{error}</p>}

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={isLoading || !isPasswordStrong}
            >
              {isLoading ? 'Güncelleniyor...' : 'Şifreyi Güncelle ve Kaydet'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
